import * as React from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  ModalVariant,
  Spinner,
  Title,
} from '@patternfly/react-core';
import { Spacer } from '@app/components/Spacer';
import { useWanContext } from '../../contexts/wan.context';
import {
  selectWanById,
} from '../../common/configuration/configuration.selectors';
import ACLProfile from '@app/api/aclprofile/aclprofile.actions';
import Site from '@app/api/site/site.actions';
import { push } from 'connected-react-router';
import {
  BullhornIcon,
  DomainIcon,
  LockIcon,
  ServiceCatalogIcon,
} from '@patternfly/react-icons';
import { IconHeading } from '@app/components/IconHeading';
import ControllerIcon from '@app/bgimages/controllers-black.svg';
import { InfoSection } from '@app/components/InfoSection';
import { actionCreators } from '@app/common/configuration/configuration.reducer';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { NotFound } from '../not-found/not-found';
import { ErrorView } from '../error/error';
import './Wan.css';
import '../../app.css';
import ThemeContext from '@app/providers/contexts/ThemeContext';
import Page from '@app/components/Page';
import { Button, Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import Loader from '@app/components/Loader';
import { MdAccountTree, MdArrowDownward, MdArrowUpward, MdUnfoldMore } from 'react-icons/md';
import { networkCidrBlock } from '@app/lib/functions';
import { 
  Column, 
  flexRender, 
  getCoreRowModel, 
  getFilteredRowModel, 
  getPaginationRowModel, 
  getSortedRowModel, 
  useReactTable 
} from '@tanstack/react-table';

type ColumnFiltersState = ColumnFilter[];
interface ColumnFilter {
  id: string;
  value: unknown;
}

interface ThemeContextType {
  theme: string;
  setTheme: (theme: string) => void;
}

export const WanView: React.FC = () => {
  const dispatch = useDispatch();
  const { id, isLoading, isFailed, isSuccess } = useWanContext();

  const wan = useSelector(selectWanById(id));
  const [sites, setSites] = React.useState<any[]>([]);
  const [sitesLoading, setSitesLoading] = React.useState(true);
  const [sitesFailed, setSitesFailed] = React.useState(false);
  const activeController = (wan as any)?.activeController;
  const standbyController = (wan as any)?.standbyController;
  const activeGateway = (wan as any)?.activeGateway;
  const standbyGateway = (wan as any)?.standbyGateway;
  const encryptedState = wan?.isEncrypted ? 'Enabled' : 'Disabled';
  const [aclProfiles, setAclProfiles] = React.useState([]);
  const { theme } = React.useContext(ThemeContext) as ThemeContextType;
  
  // Table state for sites
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

  React.useEffect(() => {
    (async function () {
      try {
        const wanSites = await Site.getWanViewSites(id);
        setSites(wanSites || []);
      } catch (error) {
        setSitesFailed(true);
      } finally {
        setSitesLoading(false);
      }
    })();
  }, [id]);

  React.useEffect(() => {
    ACLProfile.getAssignedACLs(id)
      .then(assignedAclProfiles => setAclProfiles(assignedAclProfiles || []))
      .catch(() => setAclProfiles([]));
  }, [id]);

  const editWan = React.useCallback(() => {
    dispatch(push(`/wan/edit/${id}`));
  }, [id]);

  const getControllerInfo = React.useCallback(() => {
    return [
      { label: 'Active Controller', value: activeController?.name },
      { label: 'Standby Controller', value: standbyController?.name ?? 'N/A' },
    ];
  }, [activeController, standbyController]);

  const getGatewayInfo = React.useCallback(() => {
    return [
      { label: 'Active Tunnel Gateway', value: activeGateway?.ip4Address },
      { label: 'Standby Tunnel Gateway', value: standbyGateway?.ip4Address ?? 'N/A' },
    ];
  }, [activeGateway, standbyGateway]);

  const getAclProfileInfo = () => {
    if (aclProfiles?.length > 0) {
      return aclProfiles.map(profile => {
        let aclProfile = `/aclprofile/${profile.id}`;
        return { label: <Link to={aclProfile}>{profile.name}</Link>, value: profile.description };
      });
    }
    return [];
  };

  const rebootGatewaysInWan = React.useCallback(() => {
    const serverIds = [activeGateway?.id, standbyGateway?.id].filter((x) => x !== undefined);
    if (serverIds.length > 0) {
      dispatch(actionCreators.serverRebootRequest(serverIds));
    }
  }, [activeGateway, standbyGateway, dispatch]);

  const handleRebootConfirmation = React.useCallback(() => {
    Swal.fire({
      title: 'Confirm Reboot',
      text: 'Are you sure you want to reboot the Gateway?',
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        rebootGatewaysInWan();
      }
    });
  }, [rebootGatewaysInWan]);

  const getSiteSubnets = React.useCallback(() => {
    return sites.map((site) => {
      const lan = site.SiteLans?.[0];
      const subnetAddress = `${networkCidrBlock(`${lan?.lanSegmentIp4}${lan?.lanCidrIp4}`)}${lan?.lanCidrIp4}`;
      return {
        name: (site as any).name || site.address1,
        subnet: subnetAddress,
        accessibleIps: site.ipAccessLevel || 'All',
        addresses: site.ipAccessLevel === 'Specific' ? site.accessibleIps : '',
        id: site.id,
      };
    });
  }, [sites]);

  const sitesData = React.useMemo(() => getSiteSubnets(), [getSiteSubnets]);

  const sitesColumns = React.useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <Link to={{ pathname: '/site/' + row.original.id }}>
          {row.original.name}
        </Link>
      ),
    },
    {
      accessorKey: 'subnet',
      header: 'LAN Subnet',
    },
    {
      accessorKey: 'accessibleIps',
      header: 'Accessible IPs',
    },
    {
      accessorKey: 'addresses',
      header: 'Addresses',
    },
  ], []);

  const sitesTable = useReactTable({
    data: sitesData,
    columns: sitesColumns,
    filterFns: {},
    state: {
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
    },
    manualPagination: false,
  });

  const aclProfileInfo = getAclProfileInfo();

  if (isLoading || sitesLoading) {
    return <Loader />;
  }

  if (isFailed || sitesFailed) {
    return <ErrorView title="Failed to load pWAN" message="🤖 Failed to load pWAN" />;
  }

  if (isSuccess && !wan) {
    return <NotFound />;
  }

  return (
    <React.Fragment>
      <Page
        tag="div"
        className={`cr-page px-3 pt-2 wans-list ${theme}`}
        title=""
        breadcrumbs={[
          { name: 'pWANs', url: '/wan', active: false },
          { name: wan?.name || '', active: true }
        ]}
        actionButton={<></>}
      >
        <Row>
          <Col lg={5} md={5} sm={12} xs={12}>
            <div className="p-3 h-100 sites-card-wrapper d-flex flex-column position-relative w-100">
              <Title headingLevel="h1" size="lg" style={{ paddingRight: '70px', marginBottom: '8px' }}>
                {wan?.name}
              </Title>
              <Row className="w-100">
                <Col lg={9} md={9} sm={12} xs={12}>
                  <div className="w-100">
                    <strong>pWAN ID: </strong>&nbsp;<span>{id}</span>
                    <br />
                    <strong>Description: </strong>&nbsp;<span>{wan?.description || 'No description provided'}</span>
                  </div>
                </Col>
                <Col lg={3} md={3} sm={12} xs={12} className="button-column-extend">
                  <div className="d-flex flex-column">
                    <Button 
                      className="text-white w-100 mb-2 ethica-button-green" 
                      onClick={editWan} 
                      style={{ 
                        textAlign: 'center', 
                        padding: '10px'
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      className="text-white w-100 mb-2 ethica-button-green"
                      onClick={handleRebootConfirmation}
                      style={{ 
                        textAlign: 'center', 
                        padding: '10px'
                      }}
                    >
                      Reboot Gateway
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>

          <Col lg={7} md={7} sm={12} xs={12}>
            <div className="p-3 h-100 sites-card-wrapper w-100">

              <Row className="w-100 mb-0">
                <Col lg={4} md={6} sm={12} xs={12} className="mb-3"> 
                  <Card className="h-100">
                    <CardHeader>                
                      <IconHeading
                        icon={<MdAccountTree className="icon-medium" />}
                        heading="Controllers"
                      />
                    </CardHeader>
                    <CardBody>
                        <InfoSection keyValuePairs={getControllerInfo()} showHeading={false} compact={true} />
                    </CardBody>
                  </Card>
                </Col>

                <Col lg={4} md={6} sm={12} xs={12} className="mb-3">
                  <Card className="h-100">
                    <CardHeader>
                      <IconHeading icon={<DomainIcon className="icon-medium" />} heading="Tunnel Gateway" />
                    </CardHeader>
                    <CardBody>
                      <InfoSection keyValuePairs={getGatewayInfo()} showHeading={false} compact={true} />
                    </CardBody>
                  </Card>               
                </Col>

                <Col lg={4} md={12} sm={12} xs={12} className="mb-3">    
                  <Card className="h-100">           
                    <CardHeader> 
                      <IconHeading
                          icon={<LockIcon className="icon-medium" />}
                          heading="Encryption"
                          subHeading={encryptedState}
                        />
                    </CardHeader>
                    <CardBody>
                      <p className="mb-0 text-muted small">If enabled, all tunnel traffic will be encrypted at 128-bit using a 256-bit key</p>
                    </CardBody>
                  </Card>   
                </Col>
              </Row>

            </div>
          </Col>
        </Row>

        <Row>
          <Col lg={5} md={5} sm={12} xs={12}>
            <div className="p-3 h-100 sites-card-wrapper d-flex position-relative">
              <Card className="h-100">
                <CardHeader>
                  Security Settings
                </CardHeader>
                <CardBody>
                  {isLoading ? (
                      <Spinner size="lg" />
                  ) : (
                    <Card>
                      <CardHeader>
                        <IconHeading
                          icon={<ServiceCatalogIcon className="icon-medium" />}
                          heading="ACL Profiles"
                          subHeading={aclProfileInfo?.length > 0 ? 'Enabled' : 'Disabled'}
                        />
                      </CardHeader>
                      <CardBody className="font-12">
                        {(aclProfileInfo?.length > 0) ? (
                          <div>
                            <InfoSection keyValuePairs={aclProfileInfo} showHeading={false} />
                          </div>
                        ) : (
                          <div>No ACL Profiles defined</div>
                        )}
                      </CardBody>
                    </Card>
                  )}
                </CardBody>
              </Card>
            </div>
          </Col>

          <Col lg={7} md={7} sm={12} xs={12}>
            <div className="p-3 h-100 sites-card-wrapper d-flex position-relative">
              <Card className="h-100">
                <CardHeader>                
                  Sites
                </CardHeader>
                <CardBody className="font-12">
                  {sitesData.length > 0 ? (
                    <>
                      <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          {sitesTable.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                              {headerGroup.headers.map(header => (
                                <th key={header.id} colSpan={header.colSpan}>
                                  {header.isPlaceholder ? null : (
                                    <div className="d-flex align-items-center justify-content-start">
                                      <div
                                        {...{
                                          className: header.column.getCanSort()
                                            ? 'select-none sites-header'
                                            : 'sites-header',
                                          onClick: header.column.getToggleSortingHandler(),
                                        }}
                                      >
                                        {flexRender(
                                          header.column.columnDef.header,
                                          header.getContext()
                                        )}

                                        {header.column.getCanSort() && (
                                          {
                                            asc: <MdArrowUpward className="ms-2" />,
                                            desc: <MdArrowDownward className="ms-2" />,
                                          }[header.column.getIsSorted() as string] ?? (
                                            <MdUnfoldMore className="ms-2 opacity-50" />
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody>
                          {sitesTable.getRowModel().rows.map(row => (
                            <tr key={row.id}>
                              {row.getVisibleCells().map(cell => (
                                <td key={cell.id}>
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      <div className="d-flex items-center justify-content-center align-items-center flex-wrap mt-2">
                        <div className="me-2">
                          <span className="flex items-center gap-1">
                            Go to page:
                            <input
                              type="number"
                              min="1"
                              max={sitesTable.getPageCount()}
                              defaultValue={sitesTable.getState().pagination.pageIndex + 1}
                              onChange={e => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0
                                sitesTable.setPageIndex(page)
                              }}
                              className="border p-1 rounded w-16 ms-2"
                            />
                          </span>
                        </div>
                        <div>
                          <button
                            className="border rounded p-1 me-1"
                            onClick={() => sitesTable.setPageIndex(0)}
                            disabled={!sitesTable.getCanPreviousPage()}
                          >
                            {'<<'}
                          </button>
                          <button
                            className="border rounded p-1 me-1"
                            onClick={() => sitesTable.previousPage()}
                            disabled={!sitesTable.getCanPreviousPage()}
                          >
                            {'<'}
                          </button>
                          <span className="flex items-center me-1">
                            <strong>
                              {sitesTable.getState().pagination.pageIndex + 1} of{' '}
                              {sitesTable.getPageCount()}
                            </strong>
                          </span>
                          <button
                            className="border rounded p-1 me-1"
                            onClick={() => sitesTable.nextPage()}
                            disabled={!sitesTable.getCanNextPage()}
                          >
                            {'>'}
                          </button>
                          <button
                            className="border rounded p-1 me-2"
                            onClick={() => sitesTable.setPageIndex(sitesTable.getPageCount() - 1)}
                            disabled={!sitesTable.getCanNextPage()}
                          >
                            {'>>'}
                          </button>
                        </div>
                        <div>
                          <select
                            className="form-select"
                            value={sitesTable.getState().pagination.pageSize}
                            onChange={e => {
                              sitesTable.setPageSize(Number(e.target.value))
                            }}
                          >
                            {[10, 20, 30, 40, 50].map(pageSize => (
                              <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div>No Sites configured!</div>
                  )}
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>


      </Page>
    </React.Fragment>
  );
};
